import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader } from '@Components';
import {
  useCreateSelection,
  useGetFundData,
  useGetFundMatches,
  useGetFundSelections,
  useRejectMatch,
  useDeleteSelection,
  useFundInvitesProject,
} from '@Hooks/Api';
import { useGetFundProjects } from '@Hooks/Api/useGetFundProjects';
import { useUpdateFundVotingConfiguration } from '@Hooks/Api/useUpdateFundVotingConfiguration';
import { useRouter } from '@Helpers/useRouter';
import { PathParams } from '@App/paths';
import { FundVotingConfiguration, SelectionStatusEnum, Selection } from '@Types';
import { useNotificationsContext } from '@Contexts';

import { FundManagement, VotingModalStatus } from './FundManagement';
import router from 'next/router';
import { useGetCurrentTab } from '@Hooks/useGetCurrentTab';
import { useGetFundGivingEngine } from '@Hooks/Api/useGetFundGivingEngine';

const SHORTLIST_TAB_STATUSES = [
  SelectionStatusEnum.SHORTLISTED,
  SelectionStatusEnum.UNAVAILABLE,
  SelectionStatusEnum.NO_LONGER_MATCHES,
];

const INVITATIONS_TAB_STATUSES = [
  SelectionStatusEnum.INVITED,
  SelectionStatusEnum.INVITATION_REJECTED,
];

export const FundManagementController = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [getFundData, { loading: isFundDataLoading, data: fundData }] = useGetFundData();
  const [getDueDiligenceProjects, { data: dueDiligenceProjects, loading: isDueDiligenceLoading }] =
    useGetFundProjects();
  const [
    getAwaitingPaymentProjects,
    { data: awaitingPaymentProjects, loading: isAwaitingPaymentLoading },
  ] = useGetFundProjects();
  const [getFundedProjects, { data: fundedProjects, loading: isFundedProjectsLoading }] =
    useGetFundProjects();
  const [getCompletedProjects, { data: completedProjects, loading: isCompletedProjectsLoading }] =
    useGetFundProjects();
  const [getFundGivingEngine, { data: fundGivingEngine, loading: isFundGivingEngineLoading }] =
    useGetFundGivingEngine();

  const totalDueDiligenceCount = dueDiligenceProjects?.results.length;
  const totalAwaitingPaymentCount = awaitingPaymentProjects?.results.length;
  const totalFundedProjectsCount = fundedProjects?.results.length;
  const totalCompletedProjectsCount = completedProjects?.results.length;
  const [getFundMatches, { paginatedData: matchesPaginatedResponse }] = useGetFundMatches();
  const {
    page: matchList,
    loading: isFundMatchesLoading,
    totalCount: totalMatchesCount,
  } = matchesPaginatedResponse;
  const [getFundSelections, { loading: isFundSelectionsLoading, data: fundSelectionsData }] =
    useGetFundSelections();
  const [createSelection] = useCreateSelection();
  const [rejectMatch] = useRejectMatch();
  const [deleteSelection] = useDeleteSelection();
  const [fundInvitesProject] = useFundInvitesProject();
  const [
    updateFundVotingConfiguration,
    { error: votingConfigurationUpdateErrors, loading: votingConfigurationUpdateLoading },
  ] = useUpdateFundVotingConfiguration();
  const { params } = useRouter();
  const fundId = Number(params[PathParams.FUND_ID]);
  const { success: successNotification, error: errorNotification } = useNotificationsContext();
  const [votingModalStatus, setVotingModalStatus] = useState<VotingModalStatus>(
    VotingModalStatus.CLOSED,
  );
  const { t } = useTranslation('fund');

  useEffect(() => {
    if (fundId) {
      setIsInitialized(true);
      getFundData(fundId);
      getFundGivingEngine(fundId);
    }
  }, [getFundData, fundId, getFundGivingEngine]);

  useEffect(() => {
    if (fundId) {
      getFundMatches(fundId);
    }
  }, [fundId, getFundMatches]);

  useEffect(() => {
    if (fundId) {
      getFundSelections(fundId);
    }
  }, [fundId, getFundSelections]);

  useEffect(() => {
    getDueDiligenceProjects(fundId, { detailedProjectStatus: 'DUE_DILIGENCE' });
  }, [fundId, getDueDiligenceProjects]);

  useEffect(() => {
    getAwaitingPaymentProjects(fundId, { detailedProjectStatus: 'AWAITING_PAYMENT' });
  }, [fundId, getAwaitingPaymentProjects]);

  useEffect(() => {
    getFundedProjects(fundId, { detailedProjectStatus: 'FUNDED' });
  }, [fundId, getFundedProjects]);

  useEffect(() => {
    getCompletedProjects(fundId, { detailedProjectStatus: ['COMPLETE', 'SURVEY_DONE'] });
  }, [fundId, getCompletedProjects]);

  const moveProjectToShortlist = useCallback(
    (projectId: number, projectName: string) =>
      createSelection(fundId, projectId)
        .then(() => {
          successNotification(t('management.add_to_shortlist_success', { projectName }));
          getFundMatches(fundId);
          getFundSelections(fundId);
        })
        .catch((err) => {
          errorNotification(
            err?.data?.detail[0] || t('management.add_to_shortlist_fail', { projectName }),
          );
        }),
    [
      createSelection,
      errorNotification,
      fundId,
      getFundMatches,
      getFundSelections,
      successNotification,
      t,
    ],
  );

  const rejectMatchedProject = useCallback(
    (matchId: number, projectName: string) =>
      rejectMatch(matchId)
        .then(() => {
          successNotification(t('management.reject_match_success', { projectName }));
          getFundMatches(fundId);
        })
        .catch((err) => {
          errorNotification(
            err?.data?.detail[0] || t('management.reject_match_fail', { projectName }),
          );
        }),
    [errorNotification, fundId, getFundMatches, rejectMatch, successNotification, t],
  );

  const removeProjectFromShortlist = useCallback(
    (selectionId: number, projectName: string) =>
      deleteSelection(fundId, selectionId)
        .then(() => {
          successNotification(
            t('management.remove_project_from_shortlist_success', { projectName }),
          );
          getFundMatches(fundId);
          getFundSelections(fundId);
        })
        .catch((err) => {
          errorNotification(
            err?.data?.detail[0] ||
              t('management.remove_project_from_shortlist_fail', { projectName }),
          );
        }),
    [
      deleteSelection,
      errorNotification,
      fundId,
      getFundMatches,
      getFundSelections,
      successNotification,
      t,
    ],
  );

  const inviteProjectToFund = useCallback(
    (selectionId: number, projectName: string) =>
      fundInvitesProject(fundId, selectionId)
        .then(() => {
          successNotification(t('management.invite_project_to_fund_success', { projectName }));
          getFundData(fundId);
          getFundSelections(fundId);
        })
        .catch((err) => {
          errorNotification(
            err?.data?.detail[0] || t('management.invite_project_to_fund_fail', { projectName }),
          );
        }),
    [
      errorNotification,
      fundId,
      fundInvitesProject,
      getFundData,
      getFundSelections,
      successNotification,
      t,
    ],
  );

  let shortlistedSelections: Selection[] = [];
  let invitedSelections: Selection[] = [];

  const getSelectionIdForProject = (projectId: number): number | undefined => {
    return fundSelectionsData.find((selection) => selection.project.id === projectId)?.id;
  };

  fundSelectionsData.forEach((selection) => {
    if (SHORTLIST_TAB_STATUSES.find((el) => el === selection.status)) {
      shortlistedSelections.push(selection);
    } else if (INVITATIONS_TAB_STATUSES.find((el) => el === selection.status)) {
      invitedSelections.push(selection);
    }
    // Any other statuses (like INVITATION_ACCEPTED, FUNDED, etc.) won't appear in either tab
  });

  if (fundData?.voting_configuration) {
    // We receive all the selections for all tabs in one batch from the backend,
    // so if we need the shortlist sorted by vote count for users with voting enabled,
    // we need to do it ourselves.
    shortlistedSelections = shortlistedSelections.sort((selection1, selection2) => {
      return (selection2.vote_count || 0) - (selection1.vote_count || 0);
    });
  }

  const tabs = useMemo(
    () => [
      {
        title: t('management.tabs.matches'),
        slug: 'matches',
        amount: isFundMatchesLoading ? undefined : totalMatchesCount,
      },
      {
        title: t('management.tabs.shortlist'),
        slug: 'shortlist',
        amount: isFundSelectionsLoading ? undefined : shortlistedSelections?.length,
      },
      {
        title: t('management.tabs.invitations'),
        slug: 'invitations',
        amount: isFundSelectionsLoading ? undefined : invitedSelections?.length,
      },
      {
        title: t('management.tabs.due_diligence'),
        slug: 'due-diligence',
        amount: isDueDiligenceLoading ? undefined : totalDueDiligenceCount,
      },
      {
        title: t('management.tabs.awaiting_payment'),
        slug: 'awaiting-payment',
        amount: isAwaitingPaymentLoading ? undefined : totalAwaitingPaymentCount,
      },
      {
        title: t('management.tabs.funded_projects'),
        slug: 'funded-projects',
        amount: isFundedProjectsLoading ? undefined : totalFundedProjectsCount,
      },
      {
        title: t('management.tabs.complete'),
        slug: 'complete',
        amount: isCompletedProjectsLoading ? undefined : totalCompletedProjectsCount,
      },
    ],
    [
      t,
      isFundMatchesLoading,
      totalMatchesCount,
      isFundSelectionsLoading,
      shortlistedSelections?.length,
      invitedSelections?.length,
      isDueDiligenceLoading,
      isAwaitingPaymentLoading,
      isFundedProjectsLoading,
      totalFundedProjectsCount,
      isCompletedProjectsLoading,
      totalDueDiligenceCount,
      totalAwaitingPaymentCount,
      totalCompletedProjectsCount,
    ],
  );

  // Set tab based on router and update modal state
  let currentTab = useGetCurrentTab(params.fundManageSlug?.[0], tabs);

  const setTab = useCallback(
    (tabNumber: number) => {
      return router.push(`/fund/${fundId}/manage/${tabs[tabNumber].slug}`, undefined, {
        shallow: true,
      });
    },
    [fundId, tabs],
  );

  const onVotingConfigurationSubmit = useCallback(
    (data: FundVotingConfiguration) => {
      const isCreating = fundData?.voting_configuration == null;
      const fixedData = {
        ...data,
        // react-hook-form doesn't let us use null as a value, so replace ""
        // with null before sending to the server.
        maximum_votes_per_user: data.maximum_votes_per_user || null,
      };
      updateFundVotingConfiguration(fundId, fixedData, fundData?.voting_configuration?.id).then(
        () => {
          // Refetch fund data (including voting configuration) to update the page.
          getFundData(fundId);
          if (isCreating) {
            // After creation, we show the sharing link modal.
            setVotingModalStatus(VotingModalStatus.SHARING);
          } else {
            setVotingModalStatus(VotingModalStatus.CLOSED);
          }
        },
      );
    },
    [fundId, fundData, getFundData, updateFundVotingConfiguration],
  );

  if (isFundDataLoading || !isInitialized) return <Loader />;

  return (
    <>
      {fundData && (
        <FundManagement
          fundId={fundId}
          fundData={fundData}
          tabs={tabs}
          currentTab={currentTab}
          setCurrentTab={setTab}
          isMatchesListLoading={isFundMatchesLoading || isFundSelectionsLoading}
          isSelectionsListLoading={isFundSelectionsLoading}
          shortlistedSelections={shortlistedSelections}
          invitedSelections={invitedSelections}
          dueDiligenceProjectsList={dueDiligenceProjects?.results || []}
          isDueDiligenceLoading={isDueDiligenceLoading}
          awaitingPaymentProjectsList={awaitingPaymentProjects?.results || []}
          isAwaitingPaymentLoading={isAwaitingPaymentLoading}
          fundedProjectsList={fundedProjects?.results || []}
          isFundedProjectsLoading={isFundedProjectsLoading}
          completedProjectsList={completedProjects?.results || []}
          isCompletedProjectsLoading={isCompletedProjectsLoading}
          moveProjectToShortlist={moveProjectToShortlist}
          rejectMatchedProject={rejectMatchedProject}
          removeProjectFromShortlist={removeProjectFromShortlist}
          inviteProjectToFund={inviteProjectToFund}
          onVotingConfigurationSubmit={onVotingConfigurationSubmit}
          votingModalStatus={votingModalStatus}
          setVotingModalStatus={setVotingModalStatus}
          votingConfigurationUpdateErrors={votingConfigurationUpdateErrors}
          votingConfigurationUpdateLoading={votingConfigurationUpdateLoading}
          matchesPaginatedResponse={matchesPaginatedResponse}
          fundGivingEngine={fundGivingEngine}
          isFundGivingEngineLoading={isFundGivingEngineLoading}
          getSelectionIdForProject={getSelectionIdForProject}
          refreshAwaitingPaymentProjects={getAwaitingPaymentProjects}
          refreshFundedProjects={getFundedProjects}
        />
      )}
    </>
  );
};
